import InputField from "components/fields/InputField";

const filterNumericInput = (value: string): string => {
  if (value === "") return "";
  let filtered = value.replace(/[^0-9.]/g, "");
  const parts = filtered.split(".");
  if (parts.length > 2) {
    filtered = parts[0] + "." + parts.slice(1).join("");
  }
  return filtered;
};

type FeeTemplate = {
  feetemplate_id: string;
  asset: string;
  asset_type: string;
  type: string;
  fixed: string;
  percentage: string;
  is_active: boolean;
  decimal: number;
  network_display?: string;
};

const getAssetDisplayName = (template: FeeTemplate): string => {
  if (template.network_display && template.network_display.trim() !== "") {
    return `${template.asset}-${template.network_display}`;
  }
  return template.asset;
};

const formatDecimal = (value: string, decimals: number): string => {
  if (!value || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(decimals);
};

type Props = {
  title: string;
  templates: FeeTemplate[];
  onTemplateChange: (templateId: string, field: string, value: string) => void;
  isUpdating: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

const FeeTemplateSection = ({
  title,
  templates,
  onTemplateChange,
  isUpdating,
  isOpen,
  onToggle,
}: Props) => {
  if (templates.length === 0) {
    return null;
  }

  const fiatTemplates = templates.filter((t) => t.asset_type === "fiat");
  const cryptoTemplates = templates.filter((t) => t.asset_type !== "fiat");

  return (
    <div className="card mb-4 !rounded-xl border border-gray-200 p-6">
      <button
        className="flex w-full items-center justify-between text-base font-semibold capitalize text-navy-700"
        onClick={onToggle}
      >
        <span>{title}</span>
        <svg
          className={`h-4 w-4 transform transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-4">
          {/* Fiat Section */}
          {fiatTemplates.length > 0 && (
            <div className="mb-10">
              <h4 className="mb-2 text-sm font-semibold text-navy-700">Fiat</h4>
              <div className="overflow-x-auto">
                <div className="w-full">
                  {/* Header Row */}
                  <div
                    className="MuiDataGrid-columnHeaders"
                    style={{ minHeight: 48, maxHeight: 48, lineHeight: "48px" }}
                  >
                    <div
                      className="MuiDataGrid-columnHeaderRow flex"
                      style={{ height: 48 }}
                    >
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "40%",
                          minWidth: "40%",
                          padding: "8px 16px",
                          borderTopLeftRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "30%",
                          minWidth: "30%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Percentage
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "30%",
                          minWidth: "30%",
                          padding: "8px 16px",
                          borderTopRightRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Fixed
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Body Rows */}
                  {fiatTemplates.map((template) => (
                    <div
                      key={template.feetemplate_id}
                      className="MuiDataGrid-row group flex"
                      style={{
                        minHeight: 48,
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "40%" }}
                      >
                        {getAssetDisplayName(template)}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "30%" }}
                      >
                        <InputField
                          id={`percentage-${template.feetemplate_id}`}
                          label=""
                          placeholder="0"
                          value={template.percentage}
                          onChange={(e) =>
                            onTemplateChange(
                              template.feetemplate_id,
                              "percentage",
                              filterNumericInput(e.target.value)
                            )
                          }
                          onBlur={(e) => {
                            const formatted = formatDecimal(e.target.value, 2);
                            if (formatted && formatted !== e.target.value) {
                              onTemplateChange(
                                template.feetemplate_id,
                                "percentage",
                                formatted
                              );
                            }
                          }}
                          disabled={isUpdating}
                          variant="auth"
                          extra="w-full"
                          type="text"
                          hideLabel
                        />
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "30%" }}
                      >
                        <InputField
                          id={`fixed-${template.feetemplate_id}`}
                          label=""
                          placeholder="0"
                          value={template.fixed}
                          onChange={(e) =>
                            onTemplateChange(
                              template.feetemplate_id,
                              "fixed",
                              filterNumericInput(e.target.value)
                            )
                          }
                          onBlur={(e) => {
                            const formatted = formatDecimal(
                              e.target.value,
                              template.decimal
                            );
                            if (formatted && formatted !== e.target.value) {
                              onTemplateChange(
                                template.feetemplate_id,
                                "fixed",
                                formatted
                              );
                            }
                          }}
                          disabled={isUpdating}
                          variant="auth"
                          extra="w-full"
                          type="text"
                          hideLabel
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Crypto Section */}
          {cryptoTemplates.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-navy-700">
                Crypto
              </h4>
              <div className="overflow-x-auto">
                <div className="w-full">
                  {/* Header Row */}
                  <div
                    className="MuiDataGrid-columnHeaders"
                    style={{ minHeight: 48, maxHeight: 48, lineHeight: "48px" }}
                  >
                    <div
                      className="MuiDataGrid-columnHeaderRow flex"
                      style={{ height: 48 }}
                    >
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "30%",
                          minWidth: "30%",
                          padding: "8px 16px",
                          borderTopLeftRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "25%",
                          minWidth: "25%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset Type
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "22.5%",
                          minWidth: "22.5%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Percentage
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "22.5%",
                          minWidth: "22.5%",
                          padding: "8px 16px",
                          borderTopRightRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Fixed
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Body Rows */}
                  {cryptoTemplates.map((template) => (
                    <div
                      key={template.feetemplate_id}
                      className="MuiDataGrid-row group flex"
                      style={{
                        minHeight: 48,
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "30%" }}
                      >
                        {getAssetDisplayName(template)}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "25%" }}
                      >
                        {template.asset_type}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "22.5%" }}
                      >
                        <InputField
                          id={`percentage-${template.feetemplate_id}`}
                          label=""
                          placeholder="0"
                          value={template.percentage}
                          onChange={(e) =>
                            onTemplateChange(
                              template.feetemplate_id,
                              "percentage",
                              filterNumericInput(e.target.value)
                            )
                          }
                          onBlur={(e) => {
                            const formatted = formatDecimal(e.target.value, 2);
                            if (formatted && formatted !== e.target.value) {
                              onTemplateChange(
                                template.feetemplate_id,
                                "percentage",
                                formatted
                              );
                            }
                          }}
                          disabled={isUpdating}
                          variant="auth"
                          extra="w-full"
                          type="text"
                          hideLabel
                        />
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "22.5%" }}
                      >
                        <InputField
                          id={`fixed-${template.feetemplate_id}`}
                          label=""
                          placeholder="0"
                          value={template.fixed}
                          onChange={(e) =>
                            onTemplateChange(
                              template.feetemplate_id,
                              "fixed",
                              filterNumericInput(e.target.value)
                            )
                          }
                          onBlur={(e) => {
                            const formatted = formatDecimal(
                              e.target.value,
                              template.decimal
                            );
                            if (formatted && formatted !== e.target.value) {
                              onTemplateChange(
                                template.feetemplate_id,
                                "fixed",
                                formatted
                              );
                            }
                          }}
                          disabled={isUpdating}
                          variant="auth"
                          extra="w-full"
                          type="text"
                          hideLabel
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeeTemplateSection;
